// M10/10d — Full-size modal for a single screenshot (before OR after).
// Three redundant exits — ESC, backdrop click, close X — so the owner
// never feels trapped. Body scroll is locked while the modal is open.
//
// View toggle: defaults to "zoom" (crop region around the changed element)
// because the card ALREADY showed the zoom — clicking into the modal is
// primarily "I want to see this change bigger." A pill in the header
// swaps to "full" for page-level context.

import React, { useEffect, useState } from "react"
import { CroppedFrame } from "./ScreenshotPreview.jsx"

export default function ScreenshotModal({ t, which, url, label, bbox, viewport, crop, onClose }) {
  const [view, setView] = useState(crop ? "zoom" : "full")

  useEffect(() => {
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    function onKey(e) {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    return () => {
      document.body.style.overflow = prevOverflow
      document.removeEventListener("keydown", onKey)
    }
  }, [onClose])

  const showZoom = view === "zoom" && crop

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={label}
      className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/80"
      onClick={onClose}
    >
      <div
        className="relative max-w-[92vw] max-h-[92vh] overflow-auto bg-surface-card rounded-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-4 px-5 py-3 border-b border-rule sticky top-0 bg-surface-card z-10">
          <p className="font-mono text-[10px] text-ink-tertiary" style={{ letterSpacing: "0.15em" }}>
            {label}
          </p>
          <div className="flex items-center gap-3">
            {crop && (
              <ViewToggle
                t={t}
                value={view}
                onChange={setView}
              />
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label={t("dashboard.screenshots.close")}
              className="text-ink-tertiary hover:text-ink-primary text-[22px] leading-none px-2"
            >
              ×
            </button>
          </div>
        </header>
        {showZoom ? (
          <ZoomView url={url} crop={crop} viewport={viewport} bbox={bbox} />
        ) : (
          <FullView url={url} label={label} bbox={bbox} viewport={viewport} />
        )}
      </div>
    </div>
  )
}

function ViewToggle({ t, value, onChange }) {
  const btn = (key, label) => {
    const active = value === key
    return (
      <button
        key={key}
        type="button"
        onClick={() => onChange(key)}
        aria-pressed={active}
        className={`font-mono text-[10px] px-2.5 py-1 rounded-pill transition-colors ${
          active
            ? "bg-brand text-ink-on-dark"
            : "bg-surface-subtle text-ink-secondary hover:text-ink-primary"
        }`}
        style={{ letterSpacing: "0.10em" }}
      >
        {label}
      </button>
    )
  }
  return (
    <div role="group" aria-label={t("dashboard.screenshots.zoom_toggle_aria")} className="flex items-center gap-1">
      {btn("zoom", t("dashboard.screenshots.zoom_label"))}
      {btn("full", t("dashboard.screenshots.full_label"))}
    </div>
  )
}

function ZoomView({ url, crop, viewport, bbox }) {
  // Size the zoom frame up to 80vh tall OR 88vw wide, maintaining the crop's
  // native aspect. `width: min(88vw, calc(80vh * aspect))` keeps the frame
  // bounded on both narrow + tall displays.
  const aspect = crop.cw / crop.ch
  return (
    <div className="p-5">
      <div
        className="relative bg-surface-subtle overflow-hidden rounded-card border border-rule"
        style={{
          aspectRatio: `${crop.cw} / ${crop.ch}`,
          maxHeight: "80vh",
          width: `min(88vw, calc(80vh * ${aspect}))`,
        }}
      >
        <CroppedFrame url={url} crop={crop} viewport={viewport} bbox={bbox} />
      </div>
    </div>
  )
}

function FullView({ url, label, bbox, viewport }) {
  return (
    <div className="relative">
      <img src={url} alt={label} draggable={false} className="block max-w-none w-auto" />
      <ModalBbox bbox={bbox} viewport={viewport} />
    </div>
  )
}

function ModalBbox({ bbox, viewport }) {
  if (!bbox || !viewport) return null
  const vw = Number(viewport.width) || 1280
  const vh = Number(viewport.height) || 800
  const w = Number(bbox.width) || 0
  const h = Number(bbox.height) || 0
  if (w <= 0 || h <= 0) return null

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      viewBox={`0 0 ${vw} ${vh}`}
      preserveAspectRatio="none"
      style={{ width: "100%", height: "100%" }}
      aria-hidden
    >
      <rect
        x={Number(bbox.x) || 0}
        y={Number(bbox.y) || 0}
        width={w}
        height={h}
        fill="none"
        stroke="#0F766E"
        strokeWidth={Math.max(vh * 0.006, 4)}
      />
    </svg>
  )
}
